(() => {
  if (customElements.get('fx-app-story')) return;

  class FxAppStory extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.steps = Array.from(this.querySelectorAll('[data-app-step]'));
      this.panels = Array.from(this.querySelectorAll('[data-app-panel]'));
      this.desktopScrollSteps = Array.from(this.querySelectorAll('[data-app-desktop-scroll-step]'));
      this.mobileSteps = Array.from(this.querySelectorAll('[data-app-mobile-step]'));
      this.mobileFeaturePanels = Array.from(this.querySelectorAll('[data-app-mobile-feature-panel]'));
      this.mobileScreenPanels = Array.from(this.querySelectorAll('[data-app-mobile-screen-panel]'));
      this.mobileStatus = this.querySelector('[data-app-mobile-status]');
      if (!this.steps.length || this.steps.length !== this.panels.length) return;

      this.activeIndex = Math.max(0, this.steps.findIndex((step) => step.classList.contains('is-active')));
      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abortController = new AbortController();
      this.scrollFrame = null;
      this.queueScrollActivation = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.activateNearestStep();
        });
      };

      this.bindEvents();
      this.setActive(this.activeIndex);
      this.queueScrollActivation();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.scrollFrame);
      this.abortController?.abort();
      delete this.dataset.initialized;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-app-step]');
        if (!step) return;

        const index = Number.parseInt(step.dataset.appIndex, 10);
        this.setActive(index);
        this.scrollToStep(index);
      }, { signal });

      this.addEventListener('keydown', (event) => {
        const step = event.target.closest('[data-app-step]');
        if (!step) return;

        const currentIndex = Number.parseInt(step.dataset.appIndex, 10);
        let nextIndex = null;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = Math.min(currentIndex + 1, this.steps.length - 1);
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = Math.max(currentIndex - 1, 0);
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.steps.length - 1;
        if (nextIndex === null || nextIndex === currentIndex) return;

        event.preventDefault();
        this.steps[nextIndex].focus();
        this.setActive(nextIndex);
        this.scrollToStep(nextIndex);
      }, { signal });

      window.addEventListener('scroll', this.queueScrollActivation, { passive: true, signal });
      window.addEventListener('resize', this.queueScrollActivation, { passive: true, signal });

      this.addEventListener('shopify:block:select', (event) => {
        const selected = event.target.closest('[data-app-step], [data-app-mobile-feature-panel]');
        if (!selected) return;

        const index = Number.parseInt(selected.dataset.appIndex, 10);
        this.setActive(index);
        this.scrollToStep(index);
      }, { signal });
    }

    scrollToStep(index) {
      const targets = this.desktopQuery.matches ? this.desktopScrollSteps : this.mobileSteps;
      const target = targets[index];
      if (!target) return;

      target.scrollIntoView({
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
        block: 'center',
      });
    }

    activateNearestStep() {
      const targets = this.desktopQuery.matches ? this.desktopScrollSteps : this.mobileSteps;
      if (!targets.length) return;

      const storyRect = this.getBoundingClientRect();
      if (storyRect.bottom <= 0 || storyRect.top >= window.innerHeight) return;

      const activationLine = window.innerHeight * 0.5;
      let nearestIndex = this.activeIndex;
      let nearestDistance = Number.POSITIVE_INFINITY;

      targets.forEach((step, index) => {
        const rect = step.getBoundingClientRect();
        const distance = Math.abs(rect.top + (rect.height / 2) - activationLine);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex !== this.activeIndex) this.setActive(nearestIndex);
    }

    setActive(requestedIndex) {
      if (!Number.isFinite(requestedIndex) || !this.steps[requestedIndex]) return;
      this.activeIndex = requestedIndex;

      this.steps.forEach((step, index) => {
        const active = index === requestedIndex;
        step.classList.toggle('is-active', active);
        step.classList.toggle('is-reached', index <= requestedIndex);
        step.setAttribute('aria-selected', String(active));
        step.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, index) => {
        const active = index === requestedIndex;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });

      this.mobileSteps.forEach((step, index) => {
        const active = index === requestedIndex;
        step.classList.toggle('is-active', active);
      });

      this.mobileFeaturePanels.forEach((panel, index) => {
        const active = index === requestedIndex;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });

      this.mobileScreenPanels.forEach((panel, index) => {
        const active = index === requestedIndex;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });

      if (this.mobileStatus) {
        const current = String(requestedIndex + 1).padStart(2, '0');
        const total = String(this.steps.length).padStart(2, '0');
        this.mobileStatus.textContent = `${current} / ${total}`;
      }
    }
  }

  customElements.define('fx-app-story', FxAppStory);
})();
